import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import type { GenericField } from '@Types';
import type { FieldInitialSettings, FieldSchema, RawField } from '@FormModule';
import { useSessionStore } from '@Stores';
import { filterFieldsAccordingToTemplateHelper } from '@AnimalModule';
import { useForm, useMFieldContact } from '@FormModule';

const animalId = ref('');
const currentSchemaModels = ref<Set<GenericField>>(new Set());
const fieldsSchema = ref<FieldSchema[]>([]);
const formData = ref();
const formError = ref();
const formId = ref('');
const formList = ref();
const formName = ref('');
const formTemplateId = ref('');
const formTemplateName = ref('');
const formTitle = ref('');
const formType = ref('');
const intakeFormType = ref('');
const isDefault = ref();
const kennels = ref();
const rawFields = ref<RawField[]>([]);
const templateFields = ref();

export default function () {
  const { shelterProfile } = storeToRefs(useSessionStore());

  const { getDetailsSchema } = useForm();
  const { contactData, showCard } = useMFieldContact();

  const hasVisualKennel = computed(() => {
    if (!kennels.value) return;

    return kennels?.value?.find((kennel: any) => kennel?.name);
  });

  const fieldsSettings = ref<FieldInitialSettings>({
    kennel: {
      edit: {
        label: hasVisualKennel.value ? 'name' : '',
        options: kennels.value,
      },
      settings: {
        show: !shelterProfile.value?.use_kennel_manager,
      },
    },
    kennel_manager: {
      settings: {
        show: false,
      },
    },
    animal_name: {
      settings: {
        type: 'animal_name',
      },
    },
    animal_status: {
      edit: {
        label: 'label',
      },
      settings: {
        required: true,
      },
    },
    birthday: {
      settings: {
        estBirthday: 'estimated',
        noModelOnStart: true,
      },
    },
    age: {
      edit: {
        label: 'value',
      },
    },
    secondary_color_map: {
      edit: {
        label: 'value',
      },
    },
    tertiary_color_map: {
      edit: {
        label: 'value',
      },
    },
    breed: {
      edit: {
        options: [],
      },
      settings: {
        type: 'breeds',
      },
    },
    intake_date: {
      model: new Date(),
    },
    'google-address': {
      settings: {
        type: 'location',
      },
    },
    partner_name: {
      edit: {
        label: 'name',
        options: [],
      },
    },
    photo: {
      settings: {
        type: 'file',
        multiple: true,
        fileLimit: 5,
      },
    },
    file: {
      settings: {
        imageOnly: false,
        multiple: true,
        fileLimit: 5,
      },
    },
    secondary_breed: {
      edit: {
        options: [],
      },
      settings: {
        type: 'select',
      },
    },
    coat_length: {
      edit: {
        label: 'value',
      },
    },
    species: {
      model: [],
      edit: {
        options: [],
      },
      settings: {
        show: true,
      },
    },
    email: {
      settings: {
        type: 'contact',
      },
    },
    phone: {
      settings: {
        type: 'contact',
      },
    },
    microchip: {
      settings: {
        type: 'microchip',
      },
    },
    size: {
      edit: {
        label: 'value',
      },
    },
  });

  watch(contactData, () => updateSchemaModels(contactData.value));

  function setFormAnimal(sourceData: any, formFields: any) {
    const updatedFieldSchema = getDetailsSchema(rawFields.value, fieldsSettings.value);

    updatedFieldSchema.forEach((field) => {
      const match = sourceData[field.name];
      if (field.name === 'animal_status' || field.name === 'weight') return;

      switch (field.name) {
        case 'photo': {
          field.model = [];
          field.value = [];
          break;
        }
        case 'file': {
          field.model = [];
          field.value = [];
          break;
        }
        case 'birthday': {
          field.model = sourceData.birthday;
          field.value = sourceData.birthday;
          field.settings.estBirthday = sourceData.est_birthday;
          break;
        }
        case 'size':
        case 'coat_length':
        case 'age':
        case 'secondary_color_map':
        case 'tertiary_color_map': {
          const fullField = formFields.find((f: any) => f.name === field.name);
          const option = fullField?.values?.find((v: any) => v.value === match || v.value === match?.value);
          field.model = option || null;
          field.value = option || null;
          break;
        }
        default: {
          field.model = match;
          field.value = match;
        }
      }
    });

    fieldsSchema.value = updatedFieldSchema;
    filterFieldsAccordingToTemplateHelper(fieldsSchema, templateFields);
  }

  function setFormPeople() {
    if (fieldsSchema.value.length) return;
    showCard.value = true;

    fieldsSchema.value = getDetailsSchema(rawFields.value, fieldsSettings.value);
  }

  function updateSchemaModels(sourceData: any) {
    setFormPeople();

    fieldsSchema.value.forEach((field) => {
      const match = sourceData[field.name];

      switch (field.name) {
        case 'country': {
          field.model = {
            country: sourceData.addresses[0]?.country,
            state: sourceData.addresses[0]?.state,
          };
          field.value = {
            country: sourceData.addresses[0]?.country,
            state: sourceData.addresses[0]?.state,
          };
          break;
        }
        case 'address1': {
          field.model = sourceData.addresses[0]?.address1;
          field.value = sourceData.addresses[0]?.address1;
          break;
        }
        case 'address2': {
          field.model = sourceData.addresses[0]?.address2;
          field.value = sourceData.addresses[0]?.address2;
          break;
        }
        case 'city': {
          field.model = sourceData.addresses[0]?.city;
          field.value = sourceData.addresses[0]?.city;
          break;
        }
        case 'zip': {
          field.model = sourceData.addresses[0]?.zip;
          field.value = sourceData.addresses[0]?.zip;
          break;
        }
        case 'phone': {
          const index = sourceData.matching_phone_index || 0;
          const phoneObj = sourceData.phones?.[index] ?? sourceData.matching_phone?.[0];
          const value = phoneObj?.phone ?? '';

          field.model = value;
          field.value = value;
          break;
        }
        case 'email': {
          const index = sourceData.matching_email_index || 0;
          const emailObj = sourceData.emails?.[index] ?? sourceData.matching_email?.[0];
          const value = emailObj?.email ?? '';

          field.model = value;
          field.value = value;
          break;
        }
        default: {
          field.model = match;
          field.value = match;
        }
      }

      showCard.value = false;
    });
  }

  function resetFieldSchema() {
    currentSchemaModels.value = new Set();
    fieldsSchema.value = [];
    formData.value = null;
    formError.value = null;
    formId.value = '';
    formName.value = '';
    formTitle.value = '';
    rawFields.value = [];
  }

  return {
    animalId,
    currentSchemaModels,
    fieldsSchema,
    fieldsSettings,
    formData,
    formError,
    formId,
    formList,
    formName,
    formTemplateId,
    formTemplateName,
    formTitle,
    formType,
    intakeFormType,
    isDefault,
    kennels,
    rawFields,
    resetFieldSchema,
    setFormAnimal,
    setFormPeople,
    templateFields,
  };
}
